import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2, X } from 'lucide-react';
import Cropper, { Area } from 'react-easy-crop';
import { AppShell } from '@/components/app/AppShell';
import { CountryPicker } from '@/components/CountryPicker';
import { DeleteAccountSurvey } from '@/components/account/DeleteAccountSurvey';
import { usersApi, MeUser } from '@/lib/users';
import { authApi, PublicUser } from '@/lib/auth';
import { authApi as securityApi } from '@/lib/auth-api';
import { ShieldCheck, Copy as CopyIcon } from 'lucide-react';
import { LANGUAGES } from '@/i18n/languages';
import { markManualLanguage } from '@/i18n/manualLanguage';

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: me, isLoading } = useQuery({
    queryKey: ['users', 'me'],
    queryFn: () => usersApi.me(),
  });

  // Profile (['users','me']) and the app-shell header (['auth','me']) are two
  // separate caches. A save must update BOTH or the top-right name/avatar chip
  // stays stale until a full page reload. Merge into the auth cache so fields
  // it doesn't carry (e.g. emailVerified) are preserved.
  const syncUser = (u: MeUser) => {
    qc.setQueryData(['users', 'me'], u);
    qc.setQueryData<PublicUser | null>(['auth', 'me'], (prev) =>
      prev
        ? {
            ...prev,
            name: u.name,
            pictureUrl: u.pictureUrl,
            countryCode: u.countryCode,
            primaryLocale: u.primaryLocale,
            isAdmin: u.isAdmin,
          }
        : prev,
    );
  };

  return (
    <AppShell title={t('account.title')} subtitle={t('account.subtitle')}>
      <main className="mx-auto max-w-3xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('account.title')}</h1>
          <p className="mt-1 text-ink-600">{t('account.subtitle')}</p>
        </div>

        {isLoading || !me ? (
          <div className="text-sm text-ink-500">{t('common.loading')}</div>
        ) : (
          <>
            <ProfileSection me={me} onUpdated={syncUser} />
            <AvatarSection me={me} onUpdated={syncUser} />
            <LanguageSection me={me} onUpdated={syncUser} currentLng={i18n.language} />
            <PasswordSection hasPassword={me.hasPassword} />
            <MfaSection enabled={me.mfaEnabled === true} hasPassword={me.hasPassword} onChanged={() => qc.invalidateQueries({ queryKey: ['users', 'me'] })} />
            <DangerSection onOpenDelete={() => setDeleteOpen(true)} />
          </>
        )}
      </main>

      {me && (
        <DeleteAccountSurvey
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          hasPassword={me.hasPassword}
          onDeleted={async () => {
            // Server already cleared the session cookie. Drop client state
            // and land on the public site so nothing tries to refetch with
            // a now-invalid token.
            try { await authApi.logout(); } catch { /* ignore */ }
            qc.clear();
            navigate('/?account=deleted', { replace: true });
          }}
        />
      )}
    </AppShell>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-ink-600">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ProfileSection({ me, onUpdated }: { me: MeUser; onUpdated: (u: MeUser) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(me.name ?? '');
  const [country, setCountry] = useState(me.countryCode ?? '');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const dirty = (name !== (me.name ?? '')) || (country !== (me.countryCode ?? ''));

  const save = useMutation({
    mutationFn: () => usersApi.updateProfile({
      name: name.trim() || undefined,
      countryCode: country || undefined,
    }),
    onSuccess: (u) => {
      onUpdated(u);
      setSavedAt(Date.now());
    },
  });

  return (
    <Card title={t('account.profile_section')} subtitle={t('account.profile_intro')}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.name_label')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.email_label')}</span>
          <input
            value={me.email ?? ''}
            disabled
            className="w-full rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-500"
          />
          <span className="block mt-1 text-[11px] text-ink-400">{t('account.email_immutable_note')}</span>
        </label>
        <label className="block md:col-span-2">
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.country_label')}</span>
          <CountryPicker value={country} onChange={setCountry} />
        </label>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
        >
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {t('common.save')}
        </button>
        {savedAt && !save.isPending && !save.isError && (
          <span className="text-xs text-emerald-600">{t('common.saved')}</span>
        )}
        {save.error && (
          <span className="text-xs text-warn-700">{(save.error as Error).message}</span>
        )}
      </div>
    </Card>
  );
}

function AvatarSection({ me, onUpdated }: { me: MeUser; onUpdated: (u: MeUser) => void }) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewBust, setPreviewBust] = useState(0);

  // Cropper state: hold the picked file as a blob URL while the user
  // pans/zooms. On Save we render to a 512x512 PNG and POST that.
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!sourceUrl || !croppedArea) throw new Error('No crop area available.');
      const blob = await renderCroppedPng(sourceUrl, croppedArea);
      const file = new File([blob], 'avatar.png', { type: 'image/png' });
      return usersApi.uploadAvatar(file);
    },
    onSuccess: (u) => {
      onUpdated(u);
      setPreviewBust(Date.now());
      closeCropper();
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as Error).message;
      setError(msg);
    },
  });

  function closeCropper() {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedArea(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError(t('account.avatar_not_image', { defaultValue: 'That file is not an image.' }) as string);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError(t('account.avatar_too_large', { defaultValue: 'Image is larger than 8 MB.' }) as string);
      return;
    }
    setError(null);
    setSourceUrl(URL.createObjectURL(f));
  }

  const initial = (me.name || me.email || '?').trim().charAt(0).toUpperCase();
  const src = me.pictureUrl
    ? (me.pictureUrl.startsWith('http')
        ? me.pictureUrl
        : `${me.pictureUrl}${previewBust ? `&_=${previewBust}` : ''}`)
    : null;

  return (
    <Card title={t('account.avatar_section')} subtitle={t('account.avatar_intro')}>
      <div className="flex items-center gap-5">
        {src ? (
          <img src={src} alt="" className="h-20 w-20 rounded-lg object-cover border border-ink-200" />
        ) : (
          <span className="inline-flex h-20 w-20 items-center justify-center rounded-lg bg-foreground text-background text-2xl font-semibold">
            {initial}
          </span>
        )}
        <div className="flex-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ink-300 text-ink-900 text-sm font-semibold hover:bg-ink-100"
          >
            <Camera className="h-4 w-4" />
            {t('account.avatar_upload')}
          </button>
          <p className="mt-2 text-[11px] text-ink-500">{t('account.avatar_hint')}</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={onFile}
          />
        </div>
      </div>

      {sourceUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-lg bg-background border border-border shadow-editorial p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">{t('account.avatar_crop_title', { defaultValue: 'Adjust your photo' })}</h3>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {t('account.avatar_crop_subtitle', { defaultValue: 'Drag to position, use the slider to zoom.' })}
                </p>
              </div>
              <button type="button" onClick={closeCropper} className="text-muted-foreground hover:text-foreground" aria-label={t('common.close')}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="relative w-full h-72 rounded-md overflow-hidden bg-black">
              <Cropper
                image={sourceUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_a, p) => setCroppedArea(p)}
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                {t('account.avatar_zoom', { defaultValue: 'Zoom' })}
              </span>
              <input
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1"
              />
            </div>
            {error && (
              <p className="mt-2 text-[12px] text-warn-700">{error}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCropper}
                disabled={upload.isPending}
                className="px-3.5 py-2 rounded-md border border-ink-300 text-ink-900 text-sm font-semibold hover:bg-ink-100 disabled:opacity-60"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                onClick={() => upload.mutate()}
                disabled={upload.isPending || !croppedArea}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-foreground text-background text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {upload.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {upload.isPending
                  ? t('account.avatar_uploading', { defaultValue: 'Uploading…' })
                  : t('account.avatar_save', { defaultValue: 'Save photo' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function LanguageSection({ me, onUpdated, currentLng }: { me: MeUser; onUpdated: (u: MeUser) => void; currentLng: string }) {
  const { t, i18n } = useTranslation();
  const value = me.primaryLocale ?? currentLng.split('-')[0];

  const save = useMutation({
    mutationFn: (locale: string) => usersApi.updateProfile({ primaryLocale: locale }),
    onSuccess: (u, locale) => {
      onUpdated(u);
      markManualLanguage();
      void i18n.changeLanguage(locale);
    },
  });

  return (
    <Card title={t('account.language_section')} subtitle={t('account.language_intro')}>
      <select
        value={value}
        onChange={(e) => save.mutate(e.target.value)}
        disabled={save.isPending}
        className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
      >
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>{l.flag} {l.nativeName}</option>
        ))}
      </select>
      {save.isPending && <span className="ml-3 inline-flex items-center text-xs text-ink-500"><Loader2 className="h-3 w-3 animate-spin mr-1" /> {t('common.saving')}</span>}
    </Card>
  );
}

function PasswordSection({ hasPassword }: { hasPassword: boolean }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const change = useMutation({
    mutationFn: () => usersApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent(''); setNext(''); setConfirm('');
      setSavedAt(Date.now());
    },
  });

  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const canSubmit = next.length >= 8 && next === confirm && (hasPassword ? current.length > 0 : true);

  // Reset success indicator if the user starts typing again
  useEffect(() => {
    if (savedAt && (current || next || confirm)) setSavedAt(null);
  }, [current, next, confirm, savedAt]);

  return (
    <Card
      title={hasPassword ? t('account.password_section') : t('account.password_section_set')}
      subtitle={hasPassword ? t('account.password_intro') : t('account.password_intro_set')}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {hasPassword && (
          <label className="block md:col-span-2">
            <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.current_password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
            />
          </label>
        )}
        <label className="block">
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.new_password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.confirm_password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
          />
        </label>
      </div>
      {tooShort && <p className="mt-2 text-xs text-warn-700">{t('account.password_too_short')}</p>}
      {mismatch && <p className="mt-2 text-xs text-warn-700">{t('account.password_mismatch')}</p>}
      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={() => change.mutate()}
          disabled={!canSubmit || change.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
        >
          {change.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {hasPassword ? t('account.password_save') : t('account.password_set_save')}
        </button>
        {savedAt && !change.isPending && !change.isError && (
          <span className="text-xs text-emerald-600">{t('common.saved')}</span>
        )}
        {change.error && (
          <span className="text-xs text-warn-700">
            {((change.error as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (change.error as Error).message}
          </span>
        )}
      </div>
    </Card>
  );
}

function DangerSection({ onOpenDelete }: { onOpenDelete: () => void }) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      await usersApi.exportData();
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-warn-500/30 bg-warn-500/5 p-6">
      <h2 className="text-lg font-semibold text-warn-700">{t('account.danger_section')}</h2>
      <p className="mt-1 text-sm text-ink-700">{t('account.danger_intro')}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          onClick={onExport}
          disabled={exporting}
          className="px-4 py-2 rounded-lg border border-ink-300 text-ink-700 text-sm font-semibold hover:bg-ink-100 transition-colors disabled:opacity-50"
        >
          {exporting ? t('common.loading') : t('account.export_button')}
        </button>
        <button
          onClick={onOpenDelete}
          className="px-4 py-2 rounded-lg border border-warn-500/40 text-warn-700 text-sm font-semibold hover:bg-warn-500 hover:text-white transition-colors"
        >
          {t('account.delete_button')}
        </button>
      </div>
      <p className="mt-2 text-xs text-ink-500">{t('account.export_hint')}</p>
    </section>
  );
}

// ---- avatar cropper helpers --------------------------------------
const AVATAR_OUT_SIZE = 512;

async function renderCroppedPng(src: string, area: Area): Promise<Blob> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUT_SIZE;
  canvas.height = AVATAR_OUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, AVATAR_OUT_SIZE, AVATAR_OUT_SIZE);
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png', 0.92),
  );
  if (!blob) throw new Error('Failed to encode PNG.');
  return blob;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

// ── MFA (TOTP) section ──────────────────────────────────────────────────────
function MfaSection({ enabled, hasPassword, onChanged }: { enabled: boolean; hasPassword: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [disablePw, setDisablePw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const inputCls = 'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40';

  const enroll = useMutation({
    mutationFn: () => securityApi.enrollMfa(),
    onSuccess: (r) => { setQr(r.qrDataUrl); setErr(null); },
    onError: (e: unknown) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? 'Failed to start enrolment'),
  });
  const confirm = useMutation({
    mutationFn: () => securityApi.confirmMfa(code.trim()),
    onSuccess: (r) => { setRecovery(r.recoveryCodes); setQr(null); setCode(''); setErr(null); onChanged(); },
    onError: (e: unknown) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? 'Invalid code'),
  });
  const disable = useMutation({
    mutationFn: () => securityApi.disableMfa(disablePw),
    onSuccess: () => { setDisablePw(''); setErr(null); onChanged(); },
    onError: (e: unknown) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? 'Failed to disable'),
  });

  return (
    <Card
      title={t('security.mfa_title', { defaultValue: 'Two-factor authentication' })}
      subtitle={t('security.mfa_intro', { defaultValue: 'Protect your account with an authenticator app (TOTP). You’ll enter a 6-digit code at sign-in.' })}
    >
      <div className="flex items-center gap-2 mb-4 text-sm">
        <ShieldCheck className={`h-4 w-4 ${enabled ? 'text-emerald-600' : 'text-ink-300'}`} />
        <span className={enabled ? 'text-emerald-700 font-semibold' : 'text-ink-500'}>
          {enabled ? t('security.mfa_on', { defaultValue: 'Enabled' }) : t('security.mfa_off', { defaultValue: 'Not enabled' })}
        </span>
      </div>

      {err && <p className="text-xs text-warn-600 mb-3">{err}</p>}

      {recovery && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 mb-4">
          <p className="text-sm font-semibold text-emerald-800">{t('security.recovery_title', { defaultValue: 'Save your recovery codes' })}</p>
          <p className="text-[12px] text-emerald-700 mb-2">{t('security.recovery_intro', { defaultValue: 'Each can be used once if you lose your device. Store them somewhere safe — they won’t be shown again.' })}</p>
          <div className="grid grid-cols-2 gap-1 num text-[13px] text-ink-800">{recovery.map((c) => <span key={c} className="bg-white rounded px-2 py-1 border border-emerald-100">{c}</span>)}</div>
          <button onClick={() => { void navigator.clipboard?.writeText(recovery.join('\n')); }} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700"><CopyIcon className="h-3.5 w-3.5" /> {t('security.copy_codes', { defaultValue: 'Copy codes' })}</button>
        </div>
      )}

      {!enabled && !qr && !recovery && (
        <button onClick={() => enroll.mutate()} disabled={enroll.isPending} className="px-4 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60">
          {enroll.isPending ? t('common.loading') : t('security.enable_mfa', { defaultValue: 'Enable two-factor' })}
        </button>
      )}

      {qr && (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">{t('security.scan_qr', { defaultValue: 'Scan this with your authenticator app, then enter the 6-digit code to confirm.' })}</p>
          <img src={qr} alt="MFA QR" className="h-44 w-44 border border-ink-200 rounded-lg" />
          <div className="flex items-center gap-2 max-w-sm">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={`num ${inputCls}`} />
            <button onClick={() => confirm.mutate()} disabled={confirm.isPending || code.trim().length !== 6} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 shrink-0">{t('security.confirm', { defaultValue: 'Confirm' })}</button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="space-y-2 max-w-sm">
          <p className="text-[13px] text-ink-600">{t('security.disable_intro', { defaultValue: 'Enter your password to turn off two-factor.' })}</p>
          <div className="flex items-center gap-2">
            <input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} disabled={!hasPassword} placeholder={t('account.current_password')} className={inputCls} />
            <button onClick={() => disable.mutate()} disabled={disable.isPending || !disablePw} className="px-4 py-2 rounded-lg border border-warn-300 text-warn-700 text-sm font-semibold hover:bg-warn-50 disabled:opacity-50 shrink-0">{t('security.disable_mfa', { defaultValue: 'Disable' })}</button>
          </div>
        </div>
      )}
    </Card>
  );
}
